import { Line } from './line';
import { LineStringReadable } from './line-string-readable';
import { PolygonReadable } from './polygon-readable';
import { Triangle } from './triangle';
import { Vec } from './vec';
import { VecLike } from './vec-like';

export abstract class Polygon<Point extends Vec> extends PolygonReadable<Point,Point[]>{
	constructor(
		points:Point[],
	){
		super(points);
	}

	// protected abstract tmpVec():Vec;

	// protected newP():Point{
	// 	return new (<any>this.points[0].constructor)();
	// }

	public newLine(a:Point, b:Point):Line<Point>{
		return new Line<Point>(a,b);
	}

	public abstract newTriangle(a:Point, b:Point, c:Point):Triangle<Point>;

	public copy(that:Polygon<Point>){
		this.points=that.points.map(p=>p.clone());
		return this;
	}

	public clear(){
		this.points.length=0;
	}

	public reverse(){
		this.points.reverse();
	}

	public transform(transformer:(p:Point)=>Point){
		for(let i=0;i<this.points.length;++i){
			this.points[i]=transformer(this.points[i]);
		}
		return this;
	}

	public translate(delta:Parameters<Point['add']>[0]){
		for(const p of this)
			p.add(delta);
		return this;
	}

	public scale(scale:Parameters<Point['mul']>[0]){
		for(const p of this)
			p.mul(scale);
		return this;
	}

	// public lines():Generator<Line<Vec,Point>,void,unknown>;
	// public lines<Out extends Point>(out?:Line<Point>):Generator<Line<Point>,void,unknown>;
	// public *lines(unique:boolean){
	// 	if(unique){
	// 		for(let i=0;i<this.points.length;++i){
	// 			const a=this.points[i];
	// 			const b=this.points[(i+1)%this.points.length];
	// 			yield new Line(a,b);
	// 		}
	// 	}else{
	// 	}
	// 	if(out instanceof Line){
	// 		const tmp=out;
	// 		out=(a,b)=>{
	// 			tmp.a=a;
	// 			tmp.b=b;
	// 			return tmp;
	// 		};
	// 	}else if(!out){
	// 		out=(a,b)=>this.newLine(a,b);
	// 	}
	// 	for(let i=0;i<this.points.length;++i){
	// 		const a=this.points[i];
	// 		const b=this.points[(i+1)%this.points.length];
	// 		yield out(a,b);
	// 	}
	// }
	public *crossPairs(){
		for(const p of this.points){
			for(const q of this.points){
				if(p===q)
					continue;
				yield <[Point,Point]>[p,q];
			}
		}
	}

	public convexTriangles():Generator<ReturnType<this['newTriangle']>,void,unknown>;
	public convexTriangles<T extends Triangle<Point>>(triangle?:Triangle<Point>):Generator<T,void,unknown>;
	public *convexTriangles(triangle?:Triangle<Point>){
		if(this.count<3)
			return;
		if(triangle){
			triangle.a=this.points.at(0)!;
			for(let i=2;i<this.points.length;++i){
				triangle.b=this.points.at(i-1)!;
				triangle.c=this.points.at(i)!;
				yield triangle;
			}
		}else{
			const a=this.points.at(0)!;
			for(let i=2;i<this.points.length;++i){
				const triangle=this.newTriangle(
					a,
					this.points.at(i-1)!,
					this.points.at(i)!
				);
				yield triangle;
			}
		}
	}

	public centroid():Point|null{
		if(this.points.length<3)
			return null;

		let p:Point|null=null;
		let areaSum=0;
		for(const t of this.convexTriangles()){
			const area=t.area();
			const centroid=t.centroid().scale(area);
			if(p)
				p.add(centroid);
			else
				p=centroid;
			areaSum+=area;
		}
		p?.invScale(areaSum);
		return p;
	}

	public fragment(wantedInterval:number){
		this.points=LineStringReadable.fragment(this.points,true,wantedInterval);
		return this;
	}

	public resample(wantedInterval:number){
		if(this.count<2){
			this.points=this.points.map(p=>p.clone());
			return this;
		}
		const length=this.len();
		const intervalCount=Math.ceil(this.len()/wantedInterval);
		const intervalLength=length/intervalCount;

		const points:Point[]=[this.points[0].clone()];
		let stepDist=intervalLength;
		let aDist=0;
		let a=this.points[0];
		for(let i=0;i<this.points.length;++i){
			const b=this.points[(i+1)%this.points.length];
			const segDist=a.distTo(b);
			const bDist=aDist+segDist;
			while(stepDist<bDist && points.length<intervalCount){
				points.push(a.clone().lerp(b,(stepDist-aDist)/segDist));
				stepDist+=intervalLength;
			}
			a=b;
			aDist=bDist;
		}
		points.push(this.at(-1)!.clone());
		this.points=points;
		return this;
	}

	public maxDistFromCentroidXY(){
		const centroid=this.centroid();
		if(!centroid) return 0;
		return Math.max(...this.points.map(p=>p.distToXY(centroid)))
	}
}

export namespace Polygon{
	export function convexHullArray<Point extends VecLike>(points:Point[]){
		points.sort((a,b)=>a.x!==b.x?a.x-b.x:a.y-b.y);

		const n=points.length;
		const hull:Point[]=[];

		for(let i=0;i<2*n;i++){
			let j=i<n?i:2*n-1-i;
			while(hull.length>=2 && removeMiddle(hull[hull.length-2], hull[hull.length-1], points[j]))
				hull.pop();
			hull.push(points[j]);
		}
		hull.pop();
		return hull;
	}

	function removeMiddle(a:VecLike, b:VecLike, c:VecLike){
		const cross=(a.x-b.x)*(c.y-b.y)-(a.y-b.y)*(c.x-b.x);
		const dot=(a.x-b.x)*(c.x-b.x)+(a.y-b.y)*(c.y-b.y);
		return cross<0 || cross==0 && dot<=0;
	}

}
